interface AppVersionInfo {
  appVersion: string;
  commitId: string;
  buildTime?: string;
}

const APP_UPDATE_PROMPT_SESSION_KEY = 'exemone:app-update-prompt-shown';
const DYNAMIC_IMPORT_ERROR_PATTERNS = [
  'failed to fetch dynamically imported module',
  'error loading dynamically imported module',
  'importing a module script failed',
  'failed to load module script',
];

function getErrorMessage(error: unknown): string {
  if (error instanceof Error) {
    return error.message;
  }
  if (typeof error === 'string') {
    return error;
  }

  return '';
}

function normalizeVersionInfo(versionInfo: Partial<AppVersionInfo>): AppVersionInfo {
  return {
    appVersion: versionInfo.appVersion ?? '',
    commitId: versionInfo.commitId ?? '',
    buildTime: versionInfo.buildTime,
  };
}

function hasVersionMismatch(server: AppVersionInfo, client: AppVersionInfo): boolean {
  const hasComparableServerValue = !!server.appVersion || !!server.commitId;
  const hasComparableClientValue = !!client.appVersion || !!client.commitId;

  if (!hasComparableServerValue || !hasComparableClientValue) {
    return false;
  }

  return server.appVersion !== client.appVersion || server.commitId !== client.commitId;
}

function getPromptShownVersion(): string | null {
  try {
    return sessionStorage.getItem(APP_UPDATE_PROMPT_SESSION_KEY);
  } catch {
    return null;
  }
}

function markUpdatePromptShown(serverVersion: AppVersionInfo): void {
  try {
    const versionKey = `${serverVersion.appVersion}:${serverVersion.commitId}`;
    sessionStorage.setItem(APP_UPDATE_PROMPT_SESSION_KEY, versionKey);
  } catch {
    // sessionStorage 사용 불가 환경(privacy mode 등)에서는 무시
  }
}

export function isDynamicImportLoadError(error: unknown): boolean {
  const message = getErrorMessage(error).toLowerCase();

  return DYNAMIC_IMPORT_ERROR_PATTERNS.some((pattern) => message.includes(pattern));
}

export function getClientAppVersionInfo(): AppVersionInfo {
  return {
    appVersion: import.meta.env.VITE_APP_VERSION ?? '',
    commitId: import.meta.env.VITE_COMMIT_ID ?? '',
  };
}

export async function fetchServerAppVersionInfo(
  versionUrl = '/version.json',
): Promise<AppVersionInfo | null> {
  try {
    const response = await fetch(`${versionUrl}?t=${Date.now()}`, {
      cache: 'no-store',
    });

    if (!response.ok) {
      return null;
    }

    const responseData = (await response.json()) as Partial<AppVersionInfo>;
    return normalizeVersionInfo(responseData);
  } catch {
    return null;
  }
}

export async function shouldShowRefreshPromptForImportError(
  error: unknown,
  versionUrl = '/version.json',
): Promise<boolean> {
  if (!isDynamicImportLoadError(error)) {
    return false;
  }

  const serverVersionInfo = await fetchServerAppVersionInfo(versionUrl);

  if (!serverVersionInfo) {
    return false;
  }

  const serverVersionKey = `${serverVersionInfo.appVersion}:${serverVersionInfo.commitId}`;
  if (getPromptShownVersion() === serverVersionKey) {
    return false;
  }

  const clientVersionInfo = getClientAppVersionInfo();
  const shouldShowPrompt = hasVersionMismatch(serverVersionInfo, clientVersionInfo);

  if (shouldShowPrompt) {
    markUpdatePromptShown(serverVersionInfo);
  }

  return shouldShowPrompt;
}
